import {UrlService, events, RunConfigService, PersonalOfferStorageService, util, StickyHeaderStack} from '@nuskin/ns-util';
import {UserService} from '@nuskin/ns-account';
import ExternalPaymentStatusEnum from './externalPaymentStatusEnum.js';
import OrderStatusEnum from '../order/OrderStatusEnum.js';
import ProcessorCode from './processorCodeEnum.js';
import OrderManager from '../order/orderManager.js';
import OrderAdapter from '../order/orderAdapter.js';
import OrderService from '../order/orderService.js';
import ErrorMessageCookieService from '../error/errorMessageCookieService.js';
import axios from 'axios';
import CartService from "../cart/cartService";
import { getCachedConfigField } from '@nuskin/configuration-sdk';

const FAILED = 'Failed';
const SUCCESS = 'Successful';

const LANDING_URL = `${UrlService.getSiteUrl()}/apps/global/services/externalPayment.html`;
const EXTERNAL_PAYMENT_NEXT_ORDER = "externalPaymentNextOrder";

let hasErrorMessageForCheckoutPageFlag = false;
let errorMessageForCheckoutPage = "";
let adrOverrideNumber = "";

const handleExternalPayment = (order, processorCode, sections, tdc) => {
    switch (processorCode) {
        // PayPal
        case ProcessorCode.PAYPAL:
            _doNewExternalPaymentProcess(order);
            break;
        case ProcessorCode.DIRECTDEBIT:
            _doNewExternalPaymentProcess(order);
            break;
        default:
            if (util.MobileUtils.isPhoneGap()) {
                _handleMobileExternalPayment(order, tdc, sections);
            }
            else {
                _doNewExternalPaymentProcess(order);
            }
            break;
    }
};

const handlePayment = (tdc, sections, adr, handleMobileExternalPayment) => {
    let doingRedirect = true;  // to indicate that we are doing redirect to a different location
    hasErrorMessageForCheckoutPageFlag = false;

    return new Promise(function(resolve, reject) {
        if (needToHandleExternalPayment() || handleMobileExternalPayment) {
            const order = OrderManager.getOrder();
            if (order) {
                doingRedirect = false;
                if (!order.externalId) {
                    // Try to read the ns order number from the redirectUrl
                    order.externalId = util.Url.getParam(order.redirectUrl, 'orderId');
                    //make sure we update the last external OrderId and save the order with the new externalId
                    OrderManager.setLastOrderId(order.externalId);
                    OrderManager.saveOrders();
                }

                if (order.externalId) {
                    if (util.Url.getParam(window.location.href, 'orderStatus') !== null) {
                        _readOrderStatus(order, tdc, sections, adr).then(redirect => {
                            doingRedirect = redirect;
                            resolve(doingRedirect);
                        }).catch(e => {reject(e)});
                    } else {
                        const processorCode = util.Url.getParam(window.location.href, 'processorCode');

                        // For Twikey we always treat it as a successful order.
                        if (processorCode === ProcessorCode.DIRECTDEBIT) {
                            doingRedirect = true;
                            _handleSuccess(order);
                            resolve(doingRedirect);
                        } else {
                            _checkExternalPaymentStatus(order, tdc, sections, adr).then(redirect => {
                                doingRedirect = redirect;
                                resolve(doingRedirect);
                            });
                        }
                    }
                } else {
                    _handleNoStatusCalled(tdc);
                    doingRedirect = false;
                    resolve(doingRedirect);
                }
            } else {
                _handleNoStatusCalled(tdc);
                doingRedirect = false;
                resolve(doingRedirect);
            }
        } else {
            doingRedirect = false;
            resolve(doingRedirect);
        }
    });
};

const needToHandleExternalPayment = () => {
    //if this flag is set we want to bypass external payment and load checkout normally
    let externalPaymentNextOrder = localStorage.getItem(EXTERNAL_PAYMENT_NEXT_ORDER);

    if (externalPaymentNextOrder === "true") {
        return false;
    }
    return util.Url.getParam(window.location.href, 'externalPaymentStatus') !== null ||
        util.Url.getParam(window.location.href, 'orderStatus') !== null;
};

const hasErrorMessageForCheckoutPage = () => {
    return hasErrorMessageForCheckoutPageFlag;
};

const displayErrorMsg = (sections) => {
    if (hasErrorMessageForCheckoutPageFlag){
        sections.general.error.addErrorMessage("error", {text: errorMessageForCheckoutPage});
        //Scroll to main error message
        //ScrollUtil.scrollToCheckoutErrorSummary();
        errorMessageForCheckoutPage = "";
        hasErrorMessageForCheckoutPageFlag = false;
    }
};

const getUrlPaymentStatus = () => {
    return util.Url.getParam(window.location.href, 'externalPaymentStatus');
};

const isValidStatus = (status) => {
    return status === ExternalPaymentStatusEnum.SUCCESS ||
        status === ExternalPaymentStatusEnum.PENDING ||
        status === ExternalPaymentStatusEnum.PROCESSING;
};

const getAdrOverrideId = () => {
    return adrOverrideNumber;
};

/*******************************************************************************************/
/*    Private functions                                                                    */
/*******************************************************************************************/

const _doNewExternalPaymentProcess = (order) => {
    // We only redirect if there is a redirect Url.
    if (order.redirectUrl) {
        // The landing page should be the checkout page.
        const protocol = window.location.href.split('://')[0];
        const domain = UrlService.extractDomain();
        const path = encodeURIComponent(CartService.getCheckoutPage({route: 'orderSummary'}));

        // Append the landing page URL
        // Example of order.redirectUrl: "redirectUrl":
        //  "https://test.nuskin.com/external-payment/api/v1/redirect?orderId=NS002761827&verify=null&ofsOrderId=0149707745"
        window.location = `${order.redirectUrl}&landingUrl=${protocol}://${domain}${path}`;
    }
};

const _handleMobileExternalPayment = (order, tdc, sections) => {
    let win = window.open(`${order.redirectUrl}&landingUrl=${LANDING_URL}`, '_blank'),
        loop;

    //Clear interval must happen at loadstart instead of loadstop
    //If you clear interval inside loadstop then the interval will only work on every other page you go to.
    win.addEventListener('loadstart', function() {
        clearInterval(loop);
    });

    // Once the InAppBrowser finishes loading
    win.addEventListener('loadstop', function() {
        //console.log("loadstop event triggered");

        // Clear out the name in localStorage for subsequent opens.
        //win.executeScript({ code: "localStorage.removeItem( 'externalPaymentStatus');" });

        // Start an interval
        loop = setInterval(() => {

            // Execute JavaScript to check for the existence of a name in the
            // child browser's localStorage.
            win.executeScript({code: 'window.location.href'},
                function(response) {
                    //console.log("Response:");
                    //console.log(response);
                    let externalPaymentStatus = util.Url.getParam(response[ 0 ], 'externalPaymentStatus');

                    // If a name was set, clear the interval and close the InAppBrowser.
                    if (externalPaymentStatus) {
                        clearInterval(loop);
                        handlePayment(tdc, sections, true).then(() => {win.close();});
                    }
                }
            );
        }, 1000);
    });
};

const _getHeaders = () => {
    const user = UserService.getUser();
    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'client_id': RunConfigService.getRunConfig().clientId,
        'Authorization': `JWT ${user.eid}`
    }
}

/**
 * Gets order status and handles success and failure
 * @param order
 * @param tdc
 * @param sections
 * @param adr
 * @return {Promise<boolean>}
 * @private
 */
const _readOrderStatus = async (order, tdc, sections, adr) => {
    let doingRedirect = false;
    const orderStatusResponse = await OrderService.pollOrderStatus(order.externalId);
    const salesOrderResponse = orderStatusResponse && orderStatusResponse.SalesOrderResponse;
    const status = salesOrderResponse && salesOrderResponse.SalesOrderDetail && salesOrderResponse.SalesOrderDetail.Status;

    if (status === OrderStatusEnum.REJECTED) {
        _handleError(orderStatusResponse, tdc);
        _paymentErrorCallback(sections);
    } else {
        doingRedirect = true;
        _handleSuccess(await OrderAdapter.toOrder(salesOrderResponse.SalesOrderDetail, adr, true));
    }
    return doingRedirect;
}

const _checkExternalPaymentStatus = async (order, tdc, sections, adr) => {
    let doingRedirect = false;
    // Read the status and display the error message.
    // Example URL: https://test.nuskin.com/external-payment/api/v1/status/NS000003894
    let url = getCachedConfigField('externalPaymentStatusServiceUrl') + order.externalId,
        runConfig = RunConfigService.getRunConfig(),
        axiosConfig = {
            params: {
                //eid: user.eid,
                locale: `${runConfig.language}_${runConfig.country}`
            },
            headers: _getHeaders()
        };
    try {
        const response = await axios.get(url, axiosConfig);
        let status = response.data;

        if (status.PaymentStatus) {
            if (status.PaymentStatus.sapOrderId) {
                // the status call should return a SAP order # (from the temporary NS #)
                order.orderId = status.PaymentStatus.sapOrderId;
            } else if (status.PaymentStatus.externalOrderId) {
                // this will be the case for the async call (SAP does not have an order #)
                order.orderId = status.PaymentStatus.externalOrderId;
            }

            if (status.PaymentStatus.adrOverrideId) {
                adrOverrideNumber = status.PaymentStatus.adrOverrideId;
                order.adrOverrideNumber = adrOverrideNumber;
            } else {
                order.adrOverrideNumber = "";
            }

            switch (status.PaymentStatus.code) {
                // Redirect to order confirmation if we have received an externalPayment
                case ExternalPaymentStatusEnum.SUCCESS:
                case ExternalPaymentStatusEnum.PENDING:
                case ExternalPaymentStatusEnum.PROCESSING:
                    doingRedirect = true;
                    // look for SalesOrderResponse here
                    if (status.salesOrderResponse) {
                        order = await OrderAdapter.toOrder(status.salesOrderResponse.SalesOrderDetail, adr, true);
                    }
                    _handleSuccess(order);
                    break;
                default:
                    _handleError(status, tdc);
                    _paymentErrorCallback(sections);
                    break;
            }
        }
    } catch (e) {
        let err = e.response.data;
        let errorMessage = "Unknown System error",
            detailMessage = null;

        if (err.messages && err.messages[0]) {
            errorMessage = err.messages[0].message;
            detailMessage = "Class-" + err.messages[0].type + " - Error: " + err.messages[0].technicalMessage;
        }
        doingRedirect = true;
        _forwardErrorToLandingPage(errorMessage, detailMessage);
    }
    return doingRedirect;
};

const _forwardErrorToLandingPage = (errorMessage, detailMessage) => {
    // store the errors
    let runConfig = RunConfigService.getRunConfig(),
        errors = ErrorMessageCookieService.getErrorMessages();

    errors.addErrorMessage("error", {text: errorMessage, details: detailMessage});
    ErrorMessageCookieService.setErrorMessages(errors);

    if (PersonalOfferStorageService.isPersonalOffer()) {
        runConfig.landing = PersonalOfferStorageService.getPersonalOffer().landingPageURL;
    }

    // go to the landing page
    console.log("Got an error... go back to landing page...");
    if (runConfig.landing && runConfig.landing.length > 0) {
        window.location = runConfig.landing;
    } else if (runConfig.cartUrl && runConfig.cartUrl.length > 0) {
        // First fallback: go to cart url designated in run config
        window.location = runConfig.cartUrl;
    } else {
        // Second fallback: go to default cart url
        CartService.gotoCartPage();
    }
};

const _handleSuccess = (order) => {
    // set the order as complete
    order.completed = true;
    StickyHeaderStack.remove();
    OrderManager.saveOrders();
    _tagCreateStatus(SUCCESS);
    CartService.goToCheckoutPage({route: 'orderconfirmation'});
};

const _handleError = (status,tdc) => {
    hasErrorMessageForCheckoutPageFlag = true;

    if (status.messages && status.messages[0]) {
        // Message for checkout page.
        errorMessageForCheckoutPage = status.messages[0].message;

        if (errorMessageForCheckoutPage === null || errorMessageForCheckoutPage.length === 0){
            errorMessageForCheckoutPage = tdc.generalPaymentErrorMessage;
        }

        // Message for developer.
        console.log(status.messages[0].technicalMessage);
    }
};

const _handleNoStatusCalled = (tdc) => {
    errorMessageForCheckoutPage = tdc.generalPaymentErrorMessage;
    hasErrorMessageForCheckoutPageFlag = true;
};

const _paymentErrorCallback = (sections) => {
    _tagCreateStatus(FAILED);
    // If we have processed an external payment we check if we need to display an error.
    if (hasErrorMessageForCheckoutPage()) {
        displayErrorMsg(sections);
    }
};

const _tagCreateStatus = (status) => {
    events.tag('checkoutPurchaseButtonClicked', {status: status});
};

const externalPaymentService = {
    handleExternalPayment,
    handlePayment,
    needToHandleExternalPayment,
    hasErrorMessageForCheckoutPage,
    displayErrorMsg,
    getUrlPaymentStatus,
    isValidStatus,
    getAdrOverrideId
};

export default externalPaymentService;
